import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import StorageService from './storage/StorageService';
import { uploadFile, getStorageUrl, deleteFile } from '@/utils/storage';
import { UploadResult, FileType } from './types';

export interface FileUploadOptions {
  fileType?: FileType;
  maxSizeInMB?: number;
  path?: string;
  allowedTypes?: string[];
  useLocalFallback?: boolean;
}

export class FileUploadService {
  private static readonly MAX_BASE64_LENGTH = 1024 * 1024; // 1MB limit for base64 URLs

  /**
   * Upload a file to storage and return the URL
   */
  static async uploadFile(
    file: File,
    bucketOrOptions: string | FileUploadOptions = 'public-images',
    path?: string,
    maxSizeMB: number = 5,
    allowedTypes: string[] = []
  ): Promise<UploadResult | null> {
    try {
      // If the second parameter is an options object
      if (typeof bucketOrOptions === 'object') {
        const options = bucketOrOptions;
        const bucket = 'public-images'; // Use the new bucket
        
        // Validate file
        const validation = StorageService.validateFile(
          file, 
          options.maxSizeInMB || 5, 
          options.allowedTypes || []
        );
        
        if (!validation.valid) {
          toast.error(validation.error || 'Invalid file');
          return null;
        }
        
        try {
          // Try to upload to Supabase Storage first
          const result = await uploadFile(file, options.path || '', bucket);
          
          if (!result) {
            throw new Error('Upload failed: No result returned');
          }
          
          return result;
        } catch (error) {
          console.error('Supabase upload failed:', error);
          
          // If Supabase upload fails and local fallback is enabled, use local storage
          if (options.useLocalFallback) {
            // Check file size before attempting local storage
            if (file.size > this.MAX_BASE64_LENGTH) {
              throw new Error('File too large for local storage. Please try again or use a smaller file.');
            }
            return this.saveToLocalStorage(file);
          }
          
          // If no local fallback or file too large, show appropriate error
          if (error instanceof Error) {
            if (error.message.includes('permission') || error.message.includes('policy')) {
              toast.error('You may not have permission to upload. Please try using an external URL instead.');
            } else {
              toast.error(error.message);
            }
          } else {
            toast.error('Upload failed. Please try again.');
          }
          
          throw error;
        }
      }

      // Original implementation with separate parameters
      let bucket = bucketOrOptions;
      
      // Determine the appropriate bucket based on file type
      if (file.type.includes('pdf') || 
          file.type.includes('word') || 
          file.type.includes('presentation') || 
          file.name.endsWith('.pdf') || 
          file.name.endsWith('.docx') || 
          file.name.endsWith('.pptx')) {
        bucket = 'business-plans';
      } else if (bucketOrOptions === 'documents' || bucketOrOptions === 'company-assets') {
        bucket = 'public-images';
      }
      
      // Validate file
      const validation = StorageService.validateFile(file, maxSizeMB, allowedTypes);
      if (!validation.valid) {
        toast.error(validation.error || 'Invalid file');
        return null;
      }

      try {
        // Try to upload to Supabase Storage first
        const result = await uploadFile(file, path || '', bucket);
        if (result) {
          return {
            url: result.url,
            path: result.path
          };
        }
        
        // If file is too large for local storage, show error
        if (file.size > this.MAX_BASE64_LENGTH) {
          throw new Error('File too large. Please try again with a smaller file or use an external URL.');
        }
        
        // Try local storage as last resort
        return this.saveToLocalStorage(file);
      } catch (error) {
        console.error('Upload failed:', error);
        
        if (error instanceof Error) {
          if (error.message.includes('permission') || error.message.includes('policy')) {
            toast.error('You may not have permission to upload. Please try using an external URL instead.');
          } else {
            toast.error(error.message);
          }
        } else {
          toast.error('Upload failed. Please try again.');
        }
        
        throw error;
      }
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  }

  /**
   * Save a file to local storage (browser)
   */
  private static async saveToLocalStorage(file: File): Promise<UploadResult> {
    return new Promise((resolve, reject) => {
      // Check file size before processing
      if (file.size > this.MAX_BASE64_LENGTH) {
        reject(new Error('File too large for local storage'));
        return;
      }

      const reader = new FileReader();
      
      reader.onloadend = () => {
        const base64Data = reader.result as string;
        
        // Generate a unique ID for the file
        const fileId = Date.now().toString();
        const fileName = file.name.replace(/\s+/g, '_');
        const localPath = `local-${fileId}-${fileName}`;
        
        try {
          // Store file metadata
          localStorage.setItem(`file_meta_${localPath}`, JSON.stringify({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            timestamp: Date.now()
          }));
          
          // For images, create a compressed version if needed
          if (file.type.startsWith('image/')) {
            this.compressAndStoreImage(base64Data, localPath)
              .then(compressedUrl => {
                resolve({
                  url: compressedUrl,
                  path: localPath,
                  isLocal: true
                });
              })
              .catch(error => {
                console.error('Image compression failed:', error);
                // Fall back to original if compression fails
                resolve({
                  url: base64Data,
                  path: localPath,
                  isLocal: true
                });
              });
          } else {
            // For non-images, store as is
            localStorage.setItem(`file_data_${localPath}`, base64Data);
            resolve({
              url: base64Data,
              path: localPath,
              isLocal: true
            });
          }
        } catch (e) {
          console.error('Local storage error:', e);
          reject(new Error('Failed to save file locally'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Compress and store an image
   */
  private static async compressAndStoreImage(
    base64Data: string, 
    localPath: string, 
    maxWidth: number = 1024
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get compressed image data
        const compressedData = canvas.toDataURL('image/jpeg', 0.7);
        
        try {
          localStorage.setItem(`file_data_${localPath}`, compressedData);
          resolve(compressedData);
        } catch (e) {
          reject(e);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };
      
      img.src = base64Data;
    });
  }

  /**
   * Upload a company logo
   */
  static async uploadCompanyLogo(file: File, companyId: string): Promise<UploadResult | null> {
    const path = `company/${companyId}/logo-${Date.now()}.${file.name.split('.').pop()}`;
    return this.uploadFile(file, {
      path,
      maxSizeInMB: 2,
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      useLocalFallback: true
    });
  }

  /**
   * Upload a company profile image
   */
  static async uploadCompanyProfileImage(file: File, companyId: string): Promise<UploadResult | null> {
    const path = `company/${companyId}/profile-${Date.now()}.${file.name.split('.').pop()}`;
    return this.uploadFile(file, {
      path,
      maxSizeInMB: 5,
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      useLocalFallback: true
    });
  }

  /**
   * Upload an image to a specific path
   */
  static async uploadImage(file: File, path: string): Promise<UploadResult | null> {
    return this.uploadFile(file, {
      path,
      maxSizeInMB: 5,
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
      useLocalFallback: true
    });
  }

  /**
   * Delete a file from storage
   */
  static async deleteFile(fileUrl: string, bucket: string = 'company-assets'): Promise<boolean> {
    try {
      // Check if this is a local file
      if (fileUrl.startsWith('data:') || fileUrl.startsWith('blob:')) {
        return true;
      }
      
      if (fileUrl.startsWith('local-')) {
        try {
          localStorage.removeItem(`file_data_${fileUrl}`);
          localStorage.removeItem(`file_meta_${fileUrl}`);
          return true;
        } catch (e) {
          console.error('Error deleting local file:', e);
          return false;
        }
      }
      
      // Extract path from URL for Supabase files
      const url = new URL(fileUrl);
      const pathSegments = url.pathname.split('/');
      const bucketIndex = pathSegments.findIndex(segment => segment === bucket);
      
      if (bucketIndex === -1) {
        console.error('Could not determine file path from URL');
        return false;
      }
      
      const filePath = pathSegments.slice(bucketIndex + 1).join('/');
      return await deleteFile(filePath, bucket);
    } catch (error) {
      console.error('File delete error:', error);
      toast.error('Failed to delete file');
      return false;
    }
  }

  /**
   * Upload multiple files
   */
  static async uploadMultipleFiles(
    files: File[],
    bucket: string = 'company-assets',
    path: string = '',
    maxSizeMB: number = 5,
    allowedTypes: string[] = []
  ): Promise<UploadResult[]> {
    const uploadPromises = files.map(file => 
      this.uploadFile(file, {
        path,
        maxSizeInMB: maxSizeMB,
        allowedTypes,
        useLocalFallback: true
      })
    );
    
    const results = await Promise.all(uploadPromises);
    return results.filter(result => result !== null) as UploadResult[];
  }
}

// Export the UploadResult and FileType types for use in other files
export type { UploadResult, FileType };
export default FileUploadService;
